import { observable, action } from 'mobx';
import CategoryApi from 'api/category';

class CategoryStore {
    @observable loading;
    @observable.shallow categories;
    @observable.shallow hidden;

    constructor(rootStore) {
        this.rootStore = rootStore;

        this.loading = false;
        this.categories = [];
        this.hidden = [];
    }

    @action
    requestCategories() {
        this.loading = true;

        const q = CategoryApi.listCategories();
        q.then(response => {
            this.setCategories(response.data);
        });
        return q;

        // TODO: Catch an error
    }

    @action.bound
    setCategories(categories) {
        const store = this;
        this.loading = false;
        this.categories = categories.map(({id, name, custom, categories}) => {
            // Construct parent category
            const parent = new ParentCategory(id, name, custom, store);
            parent.setCategories(categories.map(({id, name, custom}) => {
                // Construct subcategory
                const subcategory = new Subcategory(id, name, custom);
                subcategory.setParent(parent);
                return subcategory;
            }));
            return parent;
        });
    }

    @action
    requestAddCategory(category) {
        this.loading = true;

        const q = CategoryApi.create(category);
        q.then(response => this.addCategorySuccess(response.data));
        return q;
    }

    @action.bound
    addCategorySuccess(category) {
        const { id, name } = category;
        const parentCategory = new ParentCategory(id, name, true, this);
        this.pushParentCategory(parentCategory);

        this.loading = false;
    }

    @action.bound
    pushParentCategory(category) {
        // Find alphabetical position for a new category
        const index = this.categories.findIndex(item => category.name < item.name);
        if (index === -1) {
            // Add this to the back
            this.categories.push(category);
        } else {
            this.categories.splice(index, 0, category);
        }
    }

    @action
    requestHidden() {
        const q = CategoryApi.listHidden();
        q.then(response => {
            this.setHiddenCategories(response.data);
        });
        return q;
    }

    @action.bound
    setHiddenCategories(categories) {
        const store = this;
        this.hidden = categories.map(({id, name, custom, categories}) => {
            // Construct parent category
            const parent = new ParentCategory(id, name, custom, store);
            parent.setCategories(categories.map(({id, name, custom}) => {
                // Construct subcategory
                const subcategory = new Subcategory(id, name, custom);
                subcategory.setParent(parent);
                return subcategory;
            }));
            return parent;
        });
    }

    @action.bound
    copyBetweenVisibleAndHidden(parentCategoryId, hide) {
        // Find out lists
        const list1 = hide ? this.hidden : this.categories;
        const list2 = hide ? this.categories : this.hidden;

        // Try to find if hidden category exists
        let hiddenCategory = list1.find(item => item.id === parentCategoryId);
        if (hiddenCategory) {
            return hiddenCategory;
        }

        const parentCategory = list2.find(item => item.id === parentCategoryId);

        // If the category has no children, we can safely remove it
        if (parentCategory.categories.length === 0) {
            list2.remove(parentCategory);
        }

        // Clone the category
        hiddenCategory = new ParentCategory(
            parentCategory.id,
            parentCategory.name,
            parentCategory.custom,
            this
        );

        // Find alphabetical position for a new hidden category
        const index = list1.findIndex(item => hiddenCategory.name < item.name);
        if (index === -1) {
            // Add this to the back
            list1.push(hiddenCategory);
        } else {
            list1.splice(index, 0, hiddenCategory);
        }

        return hiddenCategory;
    }
}

/** A root category that is a child of CategoryStore */
export class ParentCategory {
    @observable loading;
    id;
    @observable name;
    custom;
    @observable.shallow categories = [];
    store;

    constructor(id, name, custom, store) {
        this.id = id;
        this.name = name;
        this.custom = custom;
        this.store = store;
    }

    @action
    setCategories(categories) {
        this.categories = categories;
    }

    @action
    requestEdit(category) {
        this.loading = true;

        const q = CategoryApi.edit(this.id, category);
        q.then(response => this.editSuccess(response.data));
        return q;
    }

    @action.bound
    editSuccess(category) {
        this.name = category.name;
        this.loading = false;
    }

    @action
    requestDelete() {
        this.loading = true;

        const q = CategoryApi.delete(this.id);
        q.then(this.deleteSuccess);
        return q;
    }

    @action.bound
    deleteSuccess() {
        this.store.categories.remove(this);
    }

    @action
    requestAddSubcategory(subcategory) {
        this.loading = true;

        const q = CategoryApi.create(subcategory);
        q.then(response => this.addSubcategorySuccess(response.data));
        return q;
    }

    @action.bound
    addSubcategorySuccess(subcategory) {
        const { id, name } = subcategory;
        const subcategoryInstance = new Subcategory(id, name, true);
        subcategoryInstance.setParent(this);
        this.pushSubcategory(subcategoryInstance);

        this.loading = false;
    }

    @action.bound
    pushSubcategory(category) {
        // Find alphabetical position for a new category
        const index = this.categories.findIndex(item => category.name < item.name);
        if (index === -1) {
            // Add this to the back
            this.categories.push(category);
        } else {
            this.categories.splice(index, 0, category);
        }
    }

    @action
    requestSetHidden(hidden) {
        this.loading = true;

        if (hidden) {
            const q = CategoryApi.hide(this.id);
            q.then(() => this.setHidden(true));
            return q;
        } else {
            const q = CategoryApi.unhide(this.id);
            q.then(() => this.setHidden(false));
            return q;
        }

    }

    @action.bound
    setHidden(hidden) {
        this.store.copyBetweenVisibleAndHidden(this.id, hidden);

        // Remove from previous list
        if (hidden) {
            // Hiding
            this.store.categories.remove(this);
        } else if (this.categories.length === 0) {
            // Unhiding
            // Remove from hidden list when no children are present
            this.store.hidden.remove(this);
        } else {
            this.loading = false;
        }
    }
}

/** Subcategory that is a child of ParentCategory */
export class Subcategory {
    @observable loading = false;
    id;
    @observable name;
    custom;
    parent;

    constructor(id, name, custom) {
        this.id = id;
        this.name = name;
        this.custom = custom;
    }

    @action
    setParent(parent) {
        this.parent = parent;
    }

    @action
    requestEdit(category) {
        this.loading = true;

        const q = CategoryApi.edit(this.id, category);
        q.then(response => this.editSuccess(response.data));
        return q;
    }

    @action.bound
    editSuccess(category) {
        this.name = category.name;
        this.loading = false;
    }

    @action
    requestDelete() {
        this.loading = true;

        const q = CategoryApi.delete(this.id);
        q.then(this.deleteSuccess);
        return q;
    }

    @action.bound
    deleteSuccess() {
        this.parent.categories.remove(this);
    }

    @action
    requestSetHidden(hidden) {
        this.loading = true;

        if (hidden) {
            const q = CategoryApi.hide(this.id);
            q.then(() => this.setHidden(true));
            return q;
        } else {
            const q = CategoryApi.unhide(this.id);
            q.then(() => this.setHidden(false));
            return q;
        }
    }

    @action.bound
    setHidden(hidden) {
        this.parent.categories.remove(this);
        this.parent = this.parent.store.copyBetweenVisibleAndHidden(this.parent.id, hidden);
        this.parent.pushSubcategory(this);
        this.loading = false;
    }
}

export default CategoryStore;


// WEBPACK FOOTER //
// ./src/store/categoryStore.js